// Every label map below is built from the caller's `t` so the copy follows the
// active language — never resolved at import time.
export const getActionTypesByPlatform = (t) => ({
    pluglead: [
        { value: 'webhook_call', label: t('actionTypes.webhookCallPlugLead') },
    ],
    webhook: [
        { value: 'webhook_call', label: t('actionTypes.webhookCall') },
    ],
    activecampaign: [
        { value: 'tag_add', label: t('actionTypes.tagAdd') },
        { value: 'list_add', label: t('actionTypes.listAdd') },
    ],
});

// Shared action_config contract with the backend generic webhook adapter
// (core-platform Modules/Integration — HasWebhookActionRules).
export const DEFAULT_WEBHOOK_CONFIG = {
    url: '',
    method: 'POST',
    content_type: 'application/json',
    auth: { type: 'none' },
    headers: [],
    field_mappings: [],
    static_fields: {},
    body_template: null,
};

export const WEBHOOK_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export const getWebhookAuthTypes = (t) => [
    { value: 'none', label: t('webhookBuilder.auth.types.none') },
    { value: 'bearer', label: t('webhookBuilder.auth.types.bearer') },
    { value: 'basic', label: t('webhookBuilder.auth.types.basic') },
    { value: 'api_key', label: t('webhookBuilder.auth.types.apiKey') },
];

export const getWebhookContentTypes = (t) => [
    { value: 'application/json', label: t('webhookBuilder.contentTypes.json') },
    { value: 'application/x-www-form-urlencoded', label: t('webhookBuilder.contentTypes.form') },
];

// Variables available in field mappings / body_template. The payment.* group
// resolves on payment events (payments.*) — the dispatch attaches the charge
// context; test fires substitute a sample payment object.
export const getWebhookFieldSources = (t) => [
    { value: 'email', label: t('webhookBuilder.fieldSources.email') },
    { value: 'name', label: t('webhookBuilder.fieldSources.name') },
    { value: 'contactFirstName', label: t('webhookBuilder.fieldSources.contactFirstName') },
    { value: 'phone', label: t('webhookBuilder.fieldSources.phone') },
    { value: 'payment', label: t('webhookBuilder.fieldSources.payment') },
    { value: 'payment.id', label: t('webhookBuilder.fieldSources.paymentId') },
    { value: 'payment.plan_id', label: t('webhookBuilder.fieldSources.paymentPlanId') },
    { value: 'payment.subscription_id', label: t('webhookBuilder.fieldSources.paymentSubscriptionId') },
    { value: 'payment.amount', label: t('webhookBuilder.fieldSources.paymentAmount') },
    { value: 'payment.net_amount', label: t('webhookBuilder.fieldSources.paymentNetAmount') },
    { value: 'payment.currency', label: t('webhookBuilder.fieldSources.paymentCurrency') },
    { value: 'payment.method', label: t('webhookBuilder.fieldSources.paymentMethod') },
    { value: 'payment.confirmed_at', label: t('webhookBuilder.fieldSources.paymentConfirmedAt') },
    { value: 'payment.split', label: t('webhookBuilder.fieldSources.paymentSplit') },
];

export const DEFAULT_ACTIONS_BY_PLATFORM = {
    pluglead: [{ type: 'webhook_call', config: { webhook_url: '' } }],
    webhook: [{ type: 'webhook_call', config: { ...DEFAULT_WEBHOOK_CONFIG } }],
    activecampaign: [],
};

export function getDefaultConfigForType(type, platform) {
    switch (type) {
        case 'tag_add': return { tag_id: '', tag_name: '' };
        case 'list_add': return { list_id: '' };
        default:
            // Same webhook_call type, two shapes: PlugLead keeps the legacy
            // single-URL config; the generic webhook uses the full contract.
            return platform === 'webhook'
                ? { ...DEFAULT_WEBHOOK_CONFIG }
                : { webhook_url: '' };
    }
}

export function generateDescription(t, event, planId, platform, community) {
    const triggers = {
        'payments.confirmed': t('manage.description.triggers.confirmed'),
        'payments.pix_generated': t('manage.description.triggers.pixGenerated'),
        'payments.checkout_dropped': t('manage.description.triggers.checkoutDropped'),
    };
    const destinations = {
        pluglead: 'PlugLead',
        webhook: t('manage.description.destinations.webhook'),
        activecampaign: 'ActiveCampaign',
    };

    if (!event || !platform) return null;

    const plan = community?.plans?.find(p => String(p.id) === String(planId));
    const planLabel = plan
        ? t('manage.description.inPlan', { plan: plan.label })
        : t('manage.description.anyPlan');
    const trigger = triggers[event] || event;
    const dest = destinations[platform] || platform;

    return t('manage.description.sentence', { trigger, plan: planLabel, destination: dest });
}

let _keyCounter = 0;
export function newActionKey() {
    return `ak_${++_keyCounter}_${Date.now()}`;
}
